import { ALLOWED_PHONE_PREFIXES } from './allowedPhonePrefixes';

export type Validator<T> = (value: T) => boolean;

export const validateNotEmpty = (value: string): string | null => {
    if (!notEmptyValidator(value)) {
        return 'registration.error.fieldRequired';
    }
    return null;
};

export const notEmptyValidator: Validator<string> = (value: string) => !!value && /\S+/g.test(value);

export const validateZipCode = (zipCode: string): string | null => {
    if (!zipCodeValidator(zipCode)) {
        return 'registration.error.zipCode.invalid';
    }
    return null;
};

export const zipCodeValidator: Validator<string> = (zipCode: string) => notEmptyAndLengthValidator(zipCode, 20);

export const isPhonePrefixAllowed = (phone: string): boolean => {
    const normalized = phone.trim().replace(/\s+/g, '');
    return ALLOWED_PHONE_PREFIXES.some(prefix => normalized.startsWith(prefix));
};

export const validatePhoneNumber = (phoneNumber: string): string | null => {
    if (!notEmptyValidator(phoneNumber)) {
        return 'registration.error.phoneNumber.InvalidFormat';
    }
    if (!isPhonePrefixAllowed(phoneNumber)) {
        return 'registration.error.phoneNumber.phoneNumberCountryCodeNotSupported';
    }
    return null;
};

export const phoneNumberValidator: Validator<string> = (phoneNumber: string) =>
    notEmptyValidator(phoneNumber) && /^\+[1-9][0-9 ]{5,20}$/g.test(phoneNumber) && !/\s{2,}/.test(phoneNumber);

export const validateFirstAndLastName = (name: string): string | null => {
    if (!firstAndLastNameValidator(name)) {
        return 'registration.error.zipCode.invalid';
    }
    return null;
};

export const firstAndLastNameValidator: Validator<string> = (value: string) =>
    notEmptyAndLengthValidator(value, 40) && /^[^!$^#*&/"<>|\\{}=+?%]*$/g.test(value);

export const validateCompanyName = (companyName: string): string | null => {
    if (!companyNameValidator(companyName)) {
        return 'registration.error.companyName';
    }
    return null;
};

export const companyNameValidator: Validator<string> = (companyName: string) =>
    notEmptyAndLengthValidator(companyName, 100);

export const validateStreet = (street: string): string | null => {
    if (!streetValidator(street)) {
        return 'registration.error.street';
    }
    return null;
};

// street + houseNo together can only contain at most 254 characters since both get concatenated with one space
// in-between as first line of company address which has to be <= 255 chars
export const streetValidator: Validator<string> = (street: string) => notEmptyAndLengthValidator(street, 234);

export const validateHouseNo = (houseNo: string): string | null => {
    if (!houseNoValidator(houseNo)) {
        return 'registration.error.houseNo';
    }
    return null;
};

export const houseNoValidator: Validator<string> = (houseNo: string) => notEmptyAndLengthValidator(houseNo, 20);

export const validateCity = (city: string): string | null => {
    if (!cityValidator(city)) {
        return 'registration.error.city';
    }
    return null;
};

export const cityValidator: Validator<string> = (city: string) => notEmptyAndLengthValidator(city, 40);

const notEmptyAndLengthValidator: (value: string, maxLength: number) => boolean = (
    value: string,
    maxLength: number
) => {
    return notEmptyValidator(value) && value.length <= maxLength;
};
